import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { PATHS } from "../config/paths";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Breadcrumb } from "../Breadcrumb";
import { ArrowLeft, GraduationCap } from "lucide-react";
import { toast } from "sonner";
import API from "../api/api01";

interface AddCoursePageProps {}

export default function AddCoursePage({}: AddCoursePageProps) {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: "",
    duration: "",
    department: "",
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name || !formData.duration || !formData.department) {
      toast.error("Please fill in all fields");
      return;
    }

    setLoading(true);
    try {
      await API.post("/superadmin/courses", formData);
      toast.success("Course created successfully!");
      navigate(PATHS.COURSE_MANAGEMENT);
    } catch (err: any) {
      toast.error(err.response?.data?.detail || "Error creating course");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-5xl mx-auto space-y-6">
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            size="icon"
            onClick={() => navigate(PATHS.COURSE_MANAGEMENT)}
            className="rounded-full shadow-sm"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-slate-900 dark:text-white">
              Add New Course
            </h1>
            <Breadcrumb
              items={[
                { label: "Courses", href: "/edit-courses" },
                { label: "Create" },
              ]}
              className="mt-1"
            />
          </div>
        </div>

        <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card className="bg-card border border-white/10 shadow-sm">
            <CardHeader className="border-b border-white/5">
              <CardTitle className="text-lg flex items-center gap-2">
                <GraduationCap className="h-5 w-5 text-primary" />
                Course Details
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              <div className="space-y-2">
                <Label>Course Name *</Label>
                <Input
                  placeholder="e.g. BCA, MBA"
                  className="bg-background border-white/5"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label>Duration *</Label>
                <Input
                  placeholder="e.g. 3 Years"
                  className="bg-background border-white/5"
                  value={formData.duration}
                  onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label>Department *</Label>
                <Input
                  placeholder="e.g. Computer Science"
                  className="bg-background border-white/5"
                  value={formData.department}
                  onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                />
              </div>
            </CardContent>
            <div className="p-4 border-t border-white/5">
              <Button
                type="submit"
                className="w-full bg-primary text-white"
                disabled={loading}
              >
                {loading ? "Saving..." : "Create Course"}
              </Button>
              <Button
                type="button"
                variant="ghost"
                className="w-full mt-2"
                onClick={() => navigate(PATHS.COURSE_MANAGEMENT)}
              >
                Cancel
              </Button>
            </div>
          </Card>
        </form>
      </div>
    </div>
  );
}
